import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Plus, UserCheck, UserPlus, Users, Wallet } from 'lucide-react';
import {
  api,
  ApiError,
  cash,
  type CashAccount,
  type Party,
} from '../api';
import { CashShell } from './CashShell';
import { usePinGate } from '../auth/pinGate';
import { useReadOnly } from '../ui/AppShell';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { EmptyState, RowSkeleton, SectionCard } from '../ui/components';
import { formatInr, toneFromBalance } from './format';

type StatusFilter = 'active' | 'archived' | 'all';

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

export function CashAccountsPage() {
  const [status, setStatus] = useState<StatusFilter>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const readOnly = useReadOnly();

  const accountsQ = useQuery<CashAccount[]>({
    queryKey: ['cash-accounts', status],
    queryFn: () => cash.listAccounts({ status }),
  });
  const balancesQ = useQuery<Array<{ cashAccountId: number; balance: string }>>({
    queryKey: ['cash-balances'],
    queryFn: () => cash.listBalances(),
  });
  const allAccountsQ = useQuery<CashAccount[]>({
    queryKey: ['cash-accounts', 'all'],
    queryFn: () => cash.listAccounts({ status: 'all' }),
  });

  const balancesById = useMemo(
    () => new Map((balancesQ.data ?? []).map((item) => [item.cashAccountId, item.balance])),
    [balancesQ.data],
  );

  const peopleCount = (allAccountsQ.data ?? []).length;
  const activeCount = (allAccountsQ.data ?? []).filter((account) => account.status === 'active').length;
  const archivedCount = Math.max(0, peopleCount - activeCount);
  const netBalance = useMemo(
    () => (balancesQ.data ?? []).reduce((sum, item) => sum + Number(item.balance || '0'), 0),
    [balancesQ.data],
  );

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['cash-accounts'] });
    void qc.invalidateQueries({ queryKey: ['cash-balances'] });
    void qc.invalidateQueries({ queryKey: ['cash-accounts-active'] });
  };

  const archiveMut = useMutation({
    mutationFn: (id: number) => cash.archiveAccount(id),
    onSuccess: () => {
      setPageError(null);
      invalidate();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });
  const unarchiveMut = useMutation({
    mutationFn: (id: number) => cash.unarchiveAccount(id),
    onSuccess: () => {
      setPageError(null);
      invalidate();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onArchive = (id: number) => void withPin(() => archiveMut.mutateAsync(id));
  const onUnarchive = (id: number) => void withPin(() => unarchiveMut.mutateAsync(id));

  const loading = accountsQ.isLoading || balancesQ.isLoading;
  const kpiLoading = allAccountsQ.isLoading || balancesQ.isLoading;
  const accounts = accountsQ.data ?? [];
  const isEmpty = !loading && accounts.length === 0;

  const queueTitle =
    status === 'active'
      ? activeCount > 0
        ? `${activeCount} active people ready for entries`
        : 'Add the first active person'
      : status === 'archived'
      ? archivedCount > 0
        ? `${archivedCount} archived people kept for history`
        : 'No archived people right now'
      : `${peopleCount} people across active and archived lists`;

  return (
    <CashShell
      title="People"
      subtitle="Plain rupee ledgers for the people you pay or collect from"
      actions={
        !readOnly ? (
          <Button size="sm" onClick={() => { setPageError(null); setCreateOpen(true); }}>
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>Add person</span>
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="order-2 md:order-1">
          {!kpiLoading && (
            <MetricStrip
              section="cash"
              items={[
                { label: 'People', value: String(peopleCount), note: 'total ledgers' },
                { label: 'Active', value: String(activeCount), note: 'ready for entries' },
                {
                  label: 'Net cash',
                  value: formatInr(netBalance === 0 ? '0' : netBalance.toFixed(2)),
                  note: 'across everyone',
                  tone: netBalance < 0 ? 'neg' : netBalance > 0 ? 'pos' : 'default',
                },
              ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2">
          <ActionQueueCard
            section="cash"
            eyebrow="People list"
            title={queueTitle}
            description="Keep the list filterable, thumb-friendly, and ready to open straight into the right ledger."
            tone="neutral"
            meta={`${accounts.length} shown in the current filter`}
            actions={
              <div className="flex w-full flex-wrap items-center gap-2">
                {STATUS_OPTIONS.map((option) => {
                  const active = status === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStatus(option.value)}
                      className="rounded-full border px-3 py-1.5 text-small font-medium transition-colors"
                      style={
                        active
                          ? { background: 'var(--sec-cash)', color: '#fff', borderColor: 'var(--sec-cash)' }
                          : { background: '#fff', color: 'var(--ink-muted)', borderColor: 'var(--border)' }
                      }
                    >
                      {option.label}
                    </button>
                  );
                })}
                {!readOnly && (
                  <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setCreateOpen(true)}>
                    Add person
                  </Button>
                )}
              </div>
            }
          />
        </div>

        {pageError && <ErrorBanner>{pageError}</ErrorBanner>}

        <SectionCard
          section="cash"
          title="People"
          action={<span className="text-caption text-ink-muted">{accounts.length} shown</span>}
        >
          {loading ? (
            <RowSkeleton rows={6} />
          ) : isEmpty ? (
            <EmptyState
              section="cash"
              icon={status === 'archived' ? UserCheck : UserPlus}
              title={status === 'archived' ? 'No archived people' : 'No people yet'}
              description={
                status === 'archived'
                  ? 'Archived ledgers stay here for reference once you stop using them daily.'
                  : 'Add a person to start tracking money in and out.'
              }
              action={
                !readOnly && status !== 'archived' ? (
                  <Button size="sm" onClick={() => setCreateOpen(true)}>
                    Add first person
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {accounts.map((account) => {
                const balance = balancesById.get(account.id) ?? '0';
                const busy =
                  (archiveMut.isPending && archiveMut.variables === account.id) ||
                  (unarchiveMut.isPending && unarchiveMut.variables === account.id);
                return (
                  <CashPersonTicket
                    key={account.id}
                    account={account}
                    balance={balance}
                    readOnly={readOnly}
                    busy={busy}
                    onArchive={() => onArchive(account.id)}
                    onUnarchive={() => onUnarchive(account.id)}
                  />
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>

      {createOpen && (
        <CreateCashAccountDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            invalidate();
          }}
        />
      )}
    </CashShell>
  );
}

function CashPersonTicket({
  account,
  balance,
  readOnly,
  busy,
  onArchive,
  onUnarchive,
}: {
  account: CashAccount;
  balance: string;
  readOnly: boolean;
  busy: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
}) {
  const direction = balance === '0' ? 'settled' : balance.startsWith('-') ? 'you owe them' : 'they owe you';

  return (
    <div className="overflow-hidden rounded-[24px] border border-border/70 app-shell-surface p-4 shadow-sm backdrop-blur">
      <a href={`/cash/accounts/${account.id}/ledger`} className="block">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="truncate text-small font-semibold tracking-[-0.01em] text-ink">{account.name}</div>
              {account.status === 'archived' && <Pill tone="neutral">Archived</Pill>}
            </div>
            <div className="mt-1 text-[12px] text-ink-muted">
              {account.notes?.trim() || 'Open the person ledger to record money in or money out.'}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className={`font-mono text-[1rem] font-semibold tabular-nums ${toneFromBalance(balance)}`}>
              {formatInr(balance)}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">{direction}</div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/70 pt-3">
          <span className="text-[12px] text-ink-muted">
            {account.status === 'active' ? 'Daily cash ledger' : 'History kept for reference'}
          </span>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface text-ink-muted ring-1 ring-border/60">
            <ArrowRight className="h-4 w-4" strokeWidth={1.9} />
          </span>
        </div>
      </a>

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a href={`/cash/accounts/${account.id}/ledger`} className="inline-flex flex-1">
            <Button size="sm" variant="secondary" className="w-full justify-center">
              Open ledger
            </Button>
          </a>
          {account.status === 'active' ? (
            <Button size="sm" variant="ghost" className="flex-1 justify-center" disabled={busy} onClick={onArchive}>
              {busy ? <Spinner /> : 'Archive'}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="flex-1 justify-center" disabled={busy} onClick={onUnarchive}>
              {busy ? <Spinner /> : 'Unarchive'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CreateCashAccountDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [openingBalance, setOpeningBalance] = useState('');
  const [notes, setNotes] = useState('');
  const [linkedPartyId, setLinkedPartyId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', 'all'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });

  const createMut = useMutation({
    mutationFn: () =>
      cash.createAccount({
        name: name.trim(),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        openingBalance: openingBalance.trim().length === 0 ? null : openingBalance.trim(),
        linkedPartyId: linkedPartyId === '' ? null : Number(linkedPartyId),
      }),
    onSuccess: () => onCreated(),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError('Name is required.');
      return;
    }
    createMut.mutate();
  };

  const partyOptions = [
    { value: '', label: '— none —' },
    ...(partiesQ.data ?? []).map((party) => ({
      value: String(party.id),
      label: `${party.displayName} (${party.role})`,
    })),
  ];

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] border border-border bg-surface shadow-elevated sm:rounded-[26px]">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-ink">Add a person</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </header>
        <form className="overflow-y-auto p-4" onSubmit={onSubmit}>
          <div className="space-y-3">
            <TextInput
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              name="name"
              placeholder="e.g. Manoj"
              hint="Case-insensitive unique across all people."
              autoFocus
            />
            <TextInput
              label="Starting balance (₹, optional)"
              value={openingBalance}
              onChange={(e) => setOpeningBalance(e.target.value)}
              name="openingBalance"
              placeholder="e.g. 5000"
              inputMode="decimal"
              hint="Positive = they already owe you; negative = you already owe them. Leave blank for 0."
            />
            <Select
              label="Link to a points party (optional)"
              value={linkedPartyId}
              onChange={(value) => setLinkedPartyId(value)}
              options={partyOptions}
              hint="Display-only link. Does not affect balances."
            />
            <TextArea
              label="Notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
              hint="Metadata only."
            />
          </div>

          {error && <div className="mt-3"><ErrorBanner>{error}</ErrorBanner></div>}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Add person'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={createMut.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
